/**
 * The studio's own settings — the tab the design shipped and nothing saved.
 *
 * Its predecessor had a workspace name and a default currency that wrote to
 * nowhere, so the screen taught people the setting had taken. Everything here
 * either reaches the database or says, in words, that it cannot be changed
 * from this screen and why.
 *
 * Three kinds of thing, deliberately drawn differently:
 *
 *   editable      the name and the week start, for a partner
 *   deployment    the timezone, which is an environment variable
 *   vocabulary    stages, kinds, platforms and priorities, which are CHECK
 *                 constraints and TypeScript unions rather than lists
 *
 * The third is here because its absence is the question. A settings screen
 * with no mention of stages reads as one that has not got to them yet; this
 * says they are not coming, and why.
 */

import { useEffect, useState } from 'react'
import { Button } from '../../components/Button'
import { SegmentedControl } from '../../components/FilterBar'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { useWorkspace } from '../../context/WorkspaceContext'
import { apiErrorMessage } from '../../lib/api'
import { today } from '../../lib/format'
import { Select } from '../../components/Select'
import {
  CONTENT_TYPES,
  PLATFORMS,
  PRIORITIES,
  TASK_STATUSES
} from '../../data/types'

/**
 * The four fixed vocabularies, with the reason each one is fixed.
 *
 * Not "not built yet". Every member of every one of these has code that
 * branches on it — a colour on the board, a column, a badge — so a screen that
 * let somebody add a fifth priority would be a form for producing values that
 * nothing knows how to draw.
 */
const VOCABULARIES: { label: string; values: readonly string[]; why: string }[] = [
  {
    label: 'Board stages',
    values: TASK_STATUSES,
    why: 'Each one is a column with its own colour, and the order is the order work moves in.'
  },
  {
    label: 'Kinds',
    values: CONTENT_TYPES,
    why: 'What a piece is. The server stores it as a checked value, not free text.'
  },
  {
    label: 'Platforms',
    values: PLATFORMS,
    why: 'Where a piece goes out.'
  },
  {
    label: 'Priorities',
    values: PRIORITIES,
    why: 'Four, each with its own badge. A fifth would have nothing to draw.'
  }
]

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD', 'AUD']
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]
const selectClass =
  'h-9 min-w-[10rem] rounded-lg border border-shell-border bg-shell px-3 text-sm text-shell-text-active transition-colors hover:border-shell-text focus:border-brand-400'

export function StudioTab() {
  const { name, weekStartsOn, timezone, timezoneSource, currency, fiscalYearStarts, isLoaded, save } =
    useWorkspace()
  const { currentUser } = useAuth()
  const { showToast } = useToast()

  const isPartner = currentUser?.role === 'PARTNER'
  const [draft, setDraft] = useState(name)
  const [saving, setSaving] = useState(false)

  // The field follows the server until somebody starts typing in it. Without
  // this it would sit on the fallback name for as long as the read takes.
  useEffect(() => {
    setDraft(name)
  }, [name])

  const renamed = draft.trim() !== name && draft.trim() !== ''

  async function commit(
    changes: { name?: string; weekStartsOn?: 0 | 1; currency?: string; fiscalYearStarts?: number },
    said: string
  ) {
    setSaving(true)
    try {
      await save(changes)
      showToast(said)
    } catch (err) {
      showToast(apiErrorMessage(err), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-4">
        <div>
          <p className="text-sm font-medium text-shell-text-active">Studio name</p>
          <p className="mt-0.5 text-xs text-ink-500">
            What a client sees on anything you share with them.
          </p>
        </div>

        {isPartner ? (
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              aria-label="Studio name"
              className="h-9 w-64 rounded-lg border border-shell-border bg-shell px-3 text-sm text-shell-text-active outline-none transition-colors focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
            />
            <Button
              disabled={!renamed || saving}
              onClick={() => {
                void commit({ name: draft.trim() }, 'Studio renamed.')
              }}
            >
              Save
            </Button>
            {renamed && (
              <button
                onClick={() => setDraft(name)}
                className="text-xs font-medium text-ink-400 hover:text-shell-text"
              >
                Cancel
              </button>
            )}
          </div>
        ) : (
          <Fixed value={name} note="A partner can change this." />
        )}
      </section>

      <section className="flex flex-col gap-4 border-t border-ink-100 pt-6">
        <div>
          <p className="text-sm font-medium text-shell-text-active">The week starts on</p>
          <p className="mt-0.5 text-xs text-ink-500">
            Which column the calendar opens each week in. Everybody sees the same one.
          </p>
        </div>

        {isPartner ? (
          <SegmentedControl
            value={String(weekStartsOn)}
            onChange={(v) => {
              const next = v === '0' ? 0 : 1
              if (next === weekStartsOn) return
              void commit(
                { weekStartsOn: next },
                `Weeks now start on ${next === 0 ? 'Sunday' : 'Monday'}.`
              )
            }}
            options={[
              { label: 'Monday', value: '1' },
              { label: 'Sunday', value: '0' }
            ]}
          />
        ) : (
          <Fixed
            value={weekStartsOn === 0 ? 'Sunday' : 'Monday'}
            note="A partner can change this."
          />
        )}
      </section>

      <section className="flex flex-col gap-4 border-t border-ink-100 pt-6">
        <div>
          <p className="text-sm font-medium text-shell-text-active">Currency and financial year</p>
          <p className="mt-0.5 text-xs text-ink-500">
            What the studio&rsquo;s own figures are in, and the month its year opens — for The
            month and anything reported by year. A client can still pay in something else.
          </p>
        </div>

        {isPartner ? (
          <div className="flex flex-wrap items-center gap-3">
            <Select
              aria-label="Currency"
              value={currency}
              disabled={saving}
              onChange={(e) =>
                void commit({ currency: e.target.value }, `Figures now in ${e.target.value}.`)
              }
              className={selectClass}
            >
              {(CURRENCIES.includes(currency) ? CURRENCIES : [currency, ...CURRENCIES]).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
            <Select
              aria-label="Financial year starts"
              value={fiscalYearStarts}
              disabled={saving}
              onChange={(e) => {
                const m = Number(e.target.value)
                void commit({ fiscalYearStarts: m }, `The financial year now starts in ${MONTHS[m - 1]}.`)
              }}
              className={selectClass}
            >
              {MONTHS.map((label, i) => (
                <option key={label} value={i + 1}>
                  Year starts in {label}
                </option>
              ))}
            </Select>
          </div>
        ) : (
          <Fixed
            value={`${currency} · year starts in ${MONTHS[fiscalYearStarts - 1] ?? 'April'}`}
            note="A partner can change this."
          />
        )}
      </section>

      <section className="flex flex-col gap-3 border-t border-ink-100 pt-6">
        <div>
          <p className="text-sm font-medium text-shell-text-active">Timezone</p>
          <p className="mt-0.5 text-xs text-ink-500">
            What the platform means by &ldquo;today&rdquo;, and therefore what counts as due, what
            counts as late, and which month a screen opens on.
          </p>
        </div>
        <Fixed
          value={timezone}
          note={`Set by ${timezoneSource} where the platform is deployed, not from this screen. Changing what "today" means from a web form is how a platform ends up a day out without anybody noticing.`}
        />
        <p className="text-xs text-ink-400">
          By that clock it is currently <span className="font-medium text-shell-text">{today()}</span>
          {isLoaded ? '' : ' — still checking with the server'}.
        </p>
      </section>

      <section className="flex flex-col gap-4 border-t border-ink-100 pt-6">
        <div>
          <p className="text-sm font-medium text-shell-text-active">Fixed vocabularies</p>
          <p className="mt-0.5 text-xs text-ink-500">
            These are not editable lists, and they are not a screen that has not been built yet.
            Each value has code behind it — a column, a colour, a badge — so a fifth of anything
            would be a value nothing knows how to draw. Changing one is a change to the platform.
          </p>
        </div>

        <div className="flex flex-col gap-4">
          {VOCABULARIES.map((v) => (
            <div key={v.label}>
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">
                {v.label}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {v.values.map((value) => (
                  <span
                    key={value}
                    className="rounded-md border border-shell-border bg-shell-border/40 px-2 py-0.5 text-xs font-medium text-shell-text"
                  >
                    {value}
                  </span>
                ))}
              </div>
              <p className="mt-1 text-xs text-ink-400">{v.why}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}

/** A value somebody can read and cannot change, with the reason why. */
function Fixed({ value, note }: { value: string; note: string }) {
  return (
    <div className="rounded-xl border border-shell-border bg-shell-border/40 px-3 py-2.5">
      <p className="font-mono text-sm text-shell-text-active">{value}</p>
      <p className="mt-1 text-xs text-ink-500">{note}</p>
    </div>
  )
}
